import { WA_PHYSICS } from '../core/constants';
import { SeededRNG, setGameSeed, getGameRNG } from '../core/rng';
import {
  MapGenerator,
  TerrainTheme as GeneratorTheme,
  MapComplexity,
  MapStyle,
  type MapGeneratorOptions,
} from './mapGenerator';
import { MaskCanvas, type TerrainDrawContext } from '../sim/maskCanvas';

let rng: SeededRNG = getGameRNG();

export function setTerrainSeed(seed: number): void {
  setGameSeed(seed);
  rng = getGameRNG();
}

export function createTerrainCanvas(
  width: number = WA_PHYSICS.WORLD.WIDTH,
  height: number = WA_PHYSICS.WORLD.HEIGHT,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

export type TerrainStyle =
  | 'open'
  | 'cavern'
  | 'random'
  | 'island'
  | 'bridges'
  | 'towers'
  | 'fortress'
  | 'tunnels'
  | 'platforms';
export type TerrainTheme = 'dirt' | 'snow' | 'hell' | 'jungle' | 'desert' | 'alien' | 'random';

export interface TerrainOptions {
  girders?: boolean;
  style?: TerrainStyle;
  theme?: TerrainTheme;
  complexity?: MapComplexity;
  floatingIslands?: boolean;
  bridges?: boolean;
  pillars?: boolean;
  caves?: boolean;
}

export interface GeneratedTerrainMask {
  mask: MaskCanvas;
  theme: string;
  attempts: number;
}

/**
 * Generate the authoritative collision mask for a seed. Pure TS — runs
 * identically in the browser and on the server (no canvas involved).
 */
export function generateTerrainMask(
  seed: number,
  options: TerrainOptions = {},
  width: number = WA_PHYSICS.WORLD.WIDTH,
  height: number = WA_PHYSICS.WORLD.HEIGHT,
): GeneratedTerrainMask {
  setTerrainSeed(seed);
  const mask = new MaskCanvas(width, height);
  const { theme, attempts } = generateTerrain(
    mask,
    (x, y) => mask.isSolid(x, y),
    width,
    height,
    options,
  );
  return { mask, theme, attempts };
}

/**
 * Replay the same generation onto the visible canvas for rendering. Must be
 * called with the seed and attempt count returned by generateTerrainMask so
 * the RNG sequence (and therefore the geometry) matches the collision mask.
 */
export function renderTerrainVisuals(
  canvas: HTMLCanvasElement,
  seed: number,
  options: TerrainOptions = {},
  attempts: number = 1,
): string {
  setTerrainSeed(seed);
  const ctx = canvas.getContext('2d')!;
  const theme = replayTerrain(ctx, canvas.width, canvas.height, options, attempts);
  postProcessTerrainVisual(ctx, canvas.width, canvas.height);
  return theme;
}

let noiseTile: HTMLCanvasElement | null = null;
function getNoiseTile(): HTMLCanvasElement {
  if (noiseTile) return noiseTile;
  const t = document.createElement('canvas');
  t.width = 96;
  t.height = 96;
  const tctx = t.getContext('2d')!;
  const img = tctx.createImageData(96, 96);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = Math.random() < 0.5 ? 0 : 255;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = Math.random() * 255;
  }
  tctx.putImageData(img, 0, 0);
  noiseTile = t;
  return t;
}

/**
 * Visual-only depth shading + grain over the painted terrain (collision mask
 * is rasterised separately, so this never affects physics). `source-atop`
 * keeps the effect on opaque terrain pixels only, leaving the sky untouched:
 * a soft top sheen fading to a darker "deep underground" base, plus subtle
 * grain so the ground no longer reads as a flat fill.
 */
function postProcessTerrainVisual(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  ctx.save();
  ctx.globalCompositeOperation = 'source-atop';

  const depth = ctx.createLinearGradient(0, 0, 0, height);
  depth.addColorStop(0, 'rgba(255,255,255,0.10)');
  depth.addColorStop(0.32, 'rgba(0,0,0,0)');
  depth.addColorStop(1, 'rgba(0,0,0,0.42)');
  ctx.fillStyle = depth;
  ctx.fillRect(0, 0, width, height);

  const pattern = ctx.createPattern(getNoiseTile(), 'repeat');
  if (pattern) {
    ctx.globalAlpha = 0.05;
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, width, height);
    ctx.globalAlpha = 1;
  }
  ctx.restore();
}

/**
 * Legacy single-pass generation straight onto a canvas (tests/tooling).
 * Runtime code should use generateTerrainMask + renderTerrainVisuals.
 */
export function generateProceduralTerrain(
  canvas: HTMLCanvasElement,
  options: TerrainOptions = {},
): string {
  const ctx = canvas.getContext('2d')!;
  return replayTerrain(ctx, canvas.width, canvas.height, options, 1);
}

const WA_STYLES: TerrainStyle[] = [
  'random',
  'island',
  'bridges',
  'towers',
  'fortress',
  'tunnels',
  'platforms',
];

function generateTerrain(
  ctx: TerrainDrawContext,
  isSolid: (x: number, y: number) => boolean,
  width: number,
  height: number,
  options: TerrainOptions,
): { theme: string; attempts: number } {
  const { girders = true, style = 'random', theme = 'random' } = options;

  if (style === 'cavern') {
    const generator = new MapGenerator(width, height);
    generator.generate(ctx, { mapStyle: 'cavern', theme: 'dirt', girders });
    return { theme: 'dirt', attempts: 1 };
  }

  if (WA_STYLES.includes(style)) {
    const selectedTheme = pickTheme(theme);
    const generator = new MapGenerator(width, height);
    const result = generator.generateUntilPlayable(
      ctx,
      isSolid,
      makeGeneratorOptions(selectedTheme, style as MapStyle, options),
      8,
      5,
    );
    console.log(
      `[TERRAIN] Generated ${style} (${selectedTheme}) terrain. Playable: ${result.isPlayable}, Spawn points: ${result.validSpawnPoints.length}`,
    );
    return { theme: selectedTheme, attempts: result.attempts };
  }

  drawOpenTerrain(ctx, width, height, girders);
  return { theme: theme === 'random' ? 'dirt' : theme, attempts: 1 };
}

/** Re-draws exactly `attempts` generation passes (no validation, same RNG draws). */
function replayTerrain(
  ctx: TerrainDrawContext,
  width: number,
  height: number,
  options: TerrainOptions,
  attempts: number,
): string {
  const { girders = true, style = 'random', theme = 'random' } = options;

  if (style === 'cavern') {
    const generator = new MapGenerator(width, height);
    generator.generate(ctx, { mapStyle: 'cavern', theme: 'dirt', girders });
    return 'dirt';
  }

  if (WA_STYLES.includes(style)) {
    const selectedTheme = pickTheme(theme);
    const generator = new MapGenerator(width, height);
    const generatorOptions = makeGeneratorOptions(selectedTheme, style as MapStyle, options);
    for (let i = 0; i < attempts; i++) {
      generator.generate(ctx, generatorOptions);
    }
    return selectedTheme;
  }

  drawOpenTerrain(ctx, width, height, girders);
  return theme === 'random' ? 'dirt' : theme;
}

function pickTheme(theme: TerrainTheme): GeneratorTheme {
  const themes: GeneratorTheme[] = ['dirt', 'snow', 'hell', 'jungle', 'desert', 'alien'];
  return theme === 'random' ? themes[rng.nextInt(0, themes.length - 1)] : (theme as GeneratorTheme);
}

function makeGeneratorOptions(
  theme: GeneratorTheme,
  mapStyle: MapStyle,
  options: TerrainOptions,
): MapGeneratorOptions {
  const {
    girders = true,
    complexity = 'normal',
    floatingIslands = true,
    bridges = true,
    pillars = false,
    caves = false,
  } = options;
  return {
    theme,
    complexity,
    mapStyle,
    floatingIslands,
    floatingIslandCount: { min: 2, max: 5 },
    bridges,
    pillars,
    caves,
    girders,
  };
}

function drawOpenTerrain(
  ctx: TerrainDrawContext,
  width: number,
  height: number,
  girders: boolean,
): void {
  ctx.clearRect(0, 0, width, height);

  const groundColor = '#8B4513';
  const grassColor = '#228B22';

  const baseY = height * 0.6;
  const amplitude = height * 0.15;
  const frequency = 0.005;

  const heightMap: number[] = [];
  for (let x = 0; x <= width; x++) {
    const y =
      baseY +
      Math.sin(x * frequency) * amplitude +
      Math.sin(x * frequency * 2.5) * (amplitude * 0.3) +
      Math.sin(x * frequency * 5) * (amplitude * 0.1);
    heightMap.push(y);
  }

  ctx.fillStyle = groundColor;
  ctx.beginPath();
  ctx.moveTo(0, height);
  for (let x = 0; x <= width; x++) {
    ctx.lineTo(x, heightMap[x]);
  }
  ctx.lineTo(width, height);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = grassColor;
  ctx.lineWidth = 4;
  ctx.beginPath();
  for (let x = 0; x <= width; x++) {
    if (x === 0) {
      ctx.moveTo(x, heightMap[x]);
    } else {
      ctx.lineTo(x, heightMap[x]);
    }
  }
  ctx.stroke();

  addTerrainDetails(ctx, width, height, heightMap, girders);
}

// @ts-ignore - Legacy function preserved for reference
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function generateCavernTerrain(canvas: HTMLCanvasElement, options: TerrainOptions = {}): void {
  const { girders = true } = options;
  const ctx = canvas.getContext('2d')!;
  const width = canvas.width;
  const height = canvas.height;

  ctx.clearRect(0, 0, width, height);

  const rockColor = '#5D4E37';
  const rockHighlight = '#7D6E57';
  const borderThickness = 60;
  const ceilingHeight = 80;

  ctx.fillStyle = rockColor;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = '#000000';
  ctx.globalCompositeOperation = 'destination-out';

  const ceilingY = ceilingHeight;
  const floorBaseY = height - borderThickness - 100;

  const ceilingMap: number[] = [];
  const floorMap: number[] = [];

  for (let x = 0; x <= width; x++) {
    const ceilNoise = Math.sin(x * 0.008) * 30 + Math.sin(x * 0.02) * 15 + Math.sin(x * 0.05) * 8;
    ceilingMap.push(ceilingY + ceilNoise);

    const floorNoise =
      Math.sin(x * 0.006 + 1) * 40 + Math.sin(x * 0.015 + 2) * 20 + Math.sin(x * 0.04) * 10;
    floorMap.push(floorBaseY + floorNoise);
  }

  ctx.beginPath();
  ctx.moveTo(0, ceilingMap[0]);
  for (let x = 1; x <= width; x++) {
    ctx.lineTo(x, ceilingMap[x]);
  }
  ctx.lineTo(width, floorMap[width]);
  for (let x = width; x >= 0; x--) {
    ctx.lineTo(x, floorMap[x]);
  }
  ctx.closePath();
  ctx.fill();

  const numCavities = 8 + Math.floor(rng.next() * 6);
  for (let i = 0; i < numCavities; i++) {
    const cx = borderThickness + rng.next() * (width - borderThickness * 2);
    const cy =
      ceilingMap[Math.floor(cx)] +
      50 +
      rng.next() * (floorMap[Math.floor(cx)] - ceilingMap[Math.floor(cx)] - 100);
    const rx = 40 + rng.next() * 80;
    const ry = 30 + rng.next() * 60;
    const rotation = rng.next() * Math.PI;

    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, rotation, 0, Math.PI * 2);
    ctx.fill();
  }

  const numTunnels = 3 + Math.floor(rng.next() * 3);
  for (let i = 0; i < numTunnels; i++) {
    const startX = borderThickness + rng.next() * (width - borderThickness * 2);
    const startY =
      ceilingMap[Math.floor(startX)] +
      30 +
      rng.next() * (floorMap[Math.floor(startX)] - ceilingMap[Math.floor(startX)] - 60);
    const tunnelLength = 100 + rng.next() * 200;
    const tunnelWidth = 25 + rng.next() * 30;
    const angle = rng.next() * Math.PI * 2;

    const endX = startX + Math.cos(angle) * tunnelLength;
    const endY = startY + Math.sin(angle) * tunnelLength;

    ctx.beginPath();
    ctx.lineWidth = tunnelWidth;
    ctx.lineCap = 'round';
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
  }

  ctx.globalCompositeOperation = 'source-over';

  ctx.fillStyle = rockHighlight;
  ctx.globalAlpha = 0.3;
  for (let i = 0; i < 30; i++) {
    const x = rng.next() * width;
    const y = rng.next() * height;
    const r = 5 + rng.next() * 15;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  if (girders) {
    addCavernGirders(ctx, width, height, ceilingMap, floorMap);
  }

  ctx.fillStyle = '#4169E1';
  const waterLevel = height - WA_PHYSICS.WORLD.WATER_LEVEL;
  ctx.globalAlpha = 0.7;
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillRect(0, waterLevel, width, WA_PHYSICS.WORLD.WATER_LEVEL);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
}

function addCavernGirders(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  ceilingMap: number[],
  floorMap: number[],
): void {
  const girderColor = '#666666';
  const girderBorder = '#444444';
  const girderHeight = 12;

  const numGirders = 4 + Math.floor(rng.next() * 4);

  for (let i = 0; i < numGirders; i++) {
    const x = 100 + rng.next() * (width - 200);
    const ceiling = ceilingMap[Math.floor(x)] || height * 0.2;
    const floor = floorMap[Math.floor(x)] || height * 0.8;
    const y = ceiling + 40 + rng.next() * (floor - ceiling - 80);
    const length = 80 + rng.next() * 120;
    const angle = (rng.next() - 0.5) * 30;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((angle * Math.PI) / 180);

    ctx.fillStyle = girderColor;
    ctx.fillRect(-length / 2, -girderHeight / 2, length, girderHeight);

    ctx.strokeStyle = girderBorder;
    ctx.lineWidth = 2;
    ctx.strokeRect(-length / 2, -girderHeight / 2, length, girderHeight);

    ctx.strokeStyle = '#888888';
    ctx.lineWidth = 1;
    const stripeSpacing = 20;
    for (let sx = -length / 2 + 10; sx < length / 2 - 10; sx += stripeSpacing) {
      ctx.beginPath();
      ctx.moveTo(sx, -girderHeight / 2);
      ctx.lineTo(sx + 10, girderHeight / 2);
      ctx.stroke();
    }

    ctx.restore();
  }
}

function addTerrainDetails(
  ctx: TerrainDrawContext,
  width: number,
  height: number,
  heightMap: number[],
  girders: boolean,
): void {
  ctx.fillStyle = '#654321';
  for (let i = 0; i < 20; i++) {
    const x = rng.next() * width;
    const groundY = heightMap[Math.floor(x)] || height * 0.6;
    const y = groundY + rng.next() * (height - groundY - 50);
    const w = 30 + rng.next() * 60;
    const h = 20 + rng.next() * 40;

    ctx.beginPath();
    ctx.ellipse(x, y, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  if (girders) {
    addGirders(ctx, width, height, heightMap);
  }

  ctx.fillStyle = '#4169E1';
  const waterLevel = height - WA_PHYSICS.WORLD.WATER_LEVEL;
  ctx.globalAlpha = 0.7;
  ctx.fillRect(0, waterLevel, width, WA_PHYSICS.WORLD.WATER_LEVEL);
  ctx.globalAlpha = 1;

  ctx.fillStyle = '#6495ED';
  ctx.globalAlpha = 0.3;
  for (let y = waterLevel; y < height; y += 20) {
    const waveOffset = Math.sin(y * 0.1) * 5;
    ctx.fillRect(waveOffset, y, width, 2);
  }
  ctx.globalAlpha = 1;
}

function addGirders(
  ctx: TerrainDrawContext,
  width: number,
  height: number,
  heightMap: number[],
): void {
  const girderColor = '#666666';
  const girderBorder = '#444444';
  const girderHeight = 12;

  const girders = [
    { x: width * 0.15, y: height * 0.35, length: 150, angle: 0 },
    { x: width * 0.35, y: height * 0.25, length: 120, angle: -15 },
    { x: width * 0.55, y: height * 0.3, length: 180, angle: 10 },
    { x: width * 0.75, y: height * 0.2, length: 140, angle: -5 },
    { x: width * 0.25, y: height * 0.45, length: 100, angle: 20 },
    { x: width * 0.65, y: height * 0.4, length: 130, angle: -10 },
  ];

  for (const girder of girders) {
    const minY = Math.min(
      ...heightMap.slice(
        Math.max(0, Math.floor(girder.x - girder.length / 2)),
        Math.min(width, Math.floor(girder.x + girder.length / 2)),
      ),
    );
    if (girder.y >= minY - 30) {
      girder.y = minY - 80;
    }

    ctx.save();
    ctx.translate(girder.x, girder.y);
    ctx.rotate((girder.angle * Math.PI) / 180);

    ctx.fillStyle = girderColor;
    ctx.fillRect(-girder.length / 2, -girderHeight / 2, girder.length, girderHeight);

    ctx.strokeStyle = girderBorder;
    ctx.lineWidth = 2;
    ctx.strokeRect(-girder.length / 2, -girderHeight / 2, girder.length, girderHeight);

    ctx.strokeStyle = '#888888';
    ctx.lineWidth = 1;
    const stripeSpacing = 20;
    for (let sx = -girder.length / 2 + 10; sx < girder.length / 2 - 10; sx += stripeSpacing) {
      ctx.beginPath();
      ctx.moveTo(sx, -girderHeight / 2);
      ctx.lineTo(sx + 10, girderHeight / 2);
      ctx.stroke();
    }

    ctx.restore();
  }
}

export function loadTerrainFromImage(canvas: HTMLCanvasElement, imageUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve();
    };
    img.onerror = reject;
    img.src = imageUrl;
  });
}

export function findSpawnPoints(
  isSolid: (x: number, y: number) => boolean,
  width: number,
  height: number,
  count: number,
  isCavern: boolean = false,
): Array<{ x: number; y: number }> {
  const points: Array<{ x: number; y: number }> = [];
  const minSpacing = width / (count + 1);

  for (let i = 0; i < count; i++) {
    const targetX = Math.floor(minSpacing * (i + 1) + (rng.next() - 0.5) * (minSpacing * 0.3));
    let foundGround = false;

    if (isCavern) {
      let inAir = false;
      for (let y = 0; y < height - WA_PHYSICS.WORLD.WATER_LEVEL; y++) {
        const solid = isSolid(targetX, y);

        if (!solid && !inAir) {
          inAir = true;
        }

        if (solid && inAir) {
          points.push({ x: targetX, y: y - 24 });
          foundGround = true;
          break;
        }
      }
    } else {
      for (let y = 0; y < height - WA_PHYSICS.WORLD.WATER_LEVEL; y++) {
        if (isSolid(targetX, y)) {
          points.push({ x: targetX, y: y - 24 });
          foundGround = true;
          break;
        }
      }
    }

    if (!foundGround) {
      points.push({
        x: targetX,
        y: height * 0.4,
      });
    }
  }

  console.log('Spawn points:', points);
  return points;
}
